import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { ToastContainer } from "react-toastify";
import notification from "../../../../helpers/notification";
import { TspBreadcrumb } from "../../components/primitives";
import TspLoadError from "../../components/common/TspLoadError";
import TspApiService from "../../api/tspApiService";
import { tspErrorMessage } from "../../hooks/useTspApiResource";
import useTspPermission from "../../hooks/useTspPermission";
import TSP_PERMISSIONS from "../../constants/tspPermissions";
import useTspCityVisitsData from "./useTspCityVisitsData";
import { readVisitLinkParams, toHistoryRow, toRouteCityOptions } from "./cityVisitsModel";
import CityVisitFormCard from "./components/CityVisitFormCard";
import CityVisitHistoryCard from "./components/CityVisitHistoryCard";
import CityVisitEditModal from "./components/CityVisitEditModal";

// City Visits. Visits are recorded through the API and listed from it; a visit
// only appears in the history once the server has stored it.
//
// Links from a journey's page (readVisitLinkParams):
//   ?journey=&city=&return=journey  "Visit Now": the journey and the stop arrive
//                                   selected and locked; after saving, back to the journey;
//   ?journey=&edit=&return=journey  "Update Visit": that visit opens in the edit dialog.
// Nothing in the URL is trusted: the journey must be one of the caller's
// in-progress journeys, the stop must be on its route (both re-loaded from the
// server), and the server checks all of it again when the visit is saved.
export default function TspCityVisitsPage() {
  const { search, key: locationKey } = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = useTspPermission();
  const canManage = hasPermission(TSP_PERMISSIONS.CITY_VISITS_MANAGE);
  const [link] = useState(() => readVisitLinkParams(search));
  const { journeyId, setJourneyId, setPage, journeys, route, visits, editTarget } = useTspCityVisitsData({ initialJourneyId: link.journeyId, editVisitId: link.editVisitId });
  const [editingId, setEditingId] = useState(link.editVisitId ? Number(link.editVisitId) : null);

  // Back to the journey this screen was opened from. Going BACK (not pushing a
  // new entry) re-opens the journey page, which loads it afresh from the server.
  const returnToJourney = () => {
    if (locationKey !== "default") navigate(-1);
    else navigate(`/tsp/journeys/${link.journeyId}`, { replace: true });
  };
  const returning = link.returnTo === "journey" && Boolean(link.journeyId);

  if (visits.error) return <TspLoadError error={visits.error} onRetry={visits.reload} what="city visits" />;
  if (!visits.data) return <div className="tsp-page tsp-dashboard-loading">Loading city visits…</div>;

  const handleAdd = async (payload) => {
    try {
      await TspApiService.createCityVisit(payload);
      notification("success", "City visit added.");
      if (returning && link.cityId) {
        returnToJourney();
        return { ok: true, leaving: true };
      }
      visits.reload();
      route.reload(); // the "current city" moves with the visit
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "The visit could not be saved.");
      notification("error", message);
      // Recorded meanwhile (another tab or person): show the route as it now is.
      if (error?.response?.data?.details?.code === "CITY_ALREADY_VISITED") route.reload();
      return { ok: false, message };
    }
  };

  // Back to wherever the person came from (e.g. the journey page); opened
  // directly, with nothing to go back to, it goes to the TSP dashboard.
  const goBack = () => {
    if (locationKey !== "default") navigate(-1);
    else navigate("/tsp");
  };

  const handleUpdate = async (id, payload) => {
    try {
      await TspApiService.updateCityVisit(id, payload);
      notification("success", "City visit updated.");
      setEditingId(null);
      if (returning && link.editVisitId) {
        returnToJourney();
        return { ok: true };
      }
      visits.reload();
      route.reload();
      return { ok: true };
    } catch (error) {
      const message = tspErrorMessage(error, "The visit could not be updated.");
      notification("error", message);
      return { ok: false, message };
    }
  };

  // Each year's journey is its own record, so the year tells same-named journeys apart.
  const journeyOptions = (journeys.data || []).map((journey) => ({
    id: String(journey.id),
    name: journey.year ? `${journey.journey_name} (${journey.year})` : journey.journey_name,
    startDate: journey.start_date,
    endDate: journey.end_date,
  }));
  const routeCities = toRouteCityOptions(route.data?.cities);

  // The visit being edited (from this page of the history, or loaded by id for
  // an "Update Visit" link), and its journey's dates (the in-progress journeys list carries them).
  const linkedVisit = editTarget.data && editTarget.data.id === editingId ? editTarget.data : null;
  const editingVisit = editingId ? visits.data.find((visit) => visit.id === editingId) || linkedVisit : null;
  const editingLocked = Boolean(editingVisit) && editingVisit.journey.status !== "IN_PROGRESS";
  const editingJourney = editingVisit ? journeyOptions.find((journey) => journey.id === String(editingVisit.journey.id)) : null;

  const renderForm = () => {
    if (journeys.error) return <TspLoadError error={journeys.error} onRetry={journeys.reload} what="the journeys you can record visits for" />;
    if (!journeys.data) return null;
    // "Visit Now" for a journey that cannot take a visit (ended, not started, or not the caller's).
    if (link.cityId && link.journeyId && !journeyOptions.some((journey) => journey.id === String(link.journeyId))) {
      return (
        <VisitLinkNotice journeyId={link.journeyId}>
          This journey is not in progress (or is not one you can record visits for), so a visit cannot be recorded against it.
        </VisitLinkNotice>
      );
    }
    if (journeyOptions.length === 0) {
      return (
        <div className="tsp-card">
          <p className="tsp-muted-text">No journey is in progress, so there is nothing to record a visit against. Start an approved journey first.</p>
        </div>
      );
    }
    // "Visit Now" for a stop that is not on the route, or already has its visit.
    const linkedStop = link.cityId && route.data ? routeCities.find((city) => city.id === String(link.cityId)) : null;
    if (link.cityId && route.data && !linkedStop) {
      return <VisitLinkNotice journeyId={link.journeyId}>This place is not on the journey&apos;s route.</VisitLinkNotice>;
    }
    if (linkedStop && linkedStop.status) {
      return <VisitLinkNotice journeyId={link.journeyId}>{linkedStop.label} already has a recorded visit on this journey. Open it from the journey with “View Visit”.</VisitLinkNotice>;
    }
    return (
      <>
        {route.error && <TspLoadError error={route.error} onRetry={route.reload} what="this journey's cities" />}
        <CityVisitFormCard
          journeys={journeyOptions}
          journeyId={journeyId}
          onJourneyChange={setJourneyId}
          cities={routeCities}
          citiesLoading={Boolean(journeyId) && route.loading && !route.data}
          onSubmit={handleAdd}
          initialLocationId={link.cityId || ""}
          locked={Boolean(link.cityId)}
        />
      </>
    );
  };

  return (
    <div className="tsp-page tsp-city-visits-page">
      <div className="tsp-page-heading-row">
        <div>
          <h1>City Visits</h1>
          <TspBreadcrumb items={["Dashboard", "City Visits"]} />
        </div>
        <button type="button" className="tsp-btn tsp-btn-outline-primary" onClick={goBack}>
          <ArrowLeft size={14} aria-hidden="true" /> Back
        </button>
      </div>

      {canManage && renderForm()}

      {editTarget.error && <TspLoadError error={editTarget.error} onRetry={editTarget.reload} what="the visit to update" />}
      {editingLocked && (
        <VisitLinkNotice journeyId={editingVisit.journey.id}>This visit&apos;s journey is not in progress, so the visit can no longer be edited.</VisitLinkNotice>
      )}

      <CityVisitHistoryCard records={visits.data.map(toHistoryRow)} meta={visits.meta} onPageChange={setPage} onEdit={canManage ? setEditingId : undefined} />

      {canManage && editingVisit && !editingLocked && (
        <CityVisitEditModal
          visit={editingVisit}
          range={{ startDate: editingJourney?.startDate || "", endDate: editingJourney?.endDate || "" }}
          onClose={() => setEditingId(null)}
          onSave={(payload) => handleUpdate(editingVisit.id, payload)}
        />
      )}

      <ToastContainer />
    </div>
  );
}

// Why a link from a journey cannot be followed here, with the way back.
function VisitLinkNotice({ journeyId, children }) {
  return (
    <div className="tsp-card" role="status">
      <p className="tsp-muted-text">{children}</p>
      {journeyId && (
        <Link to={`/tsp/journeys/${journeyId}`} className="tsp-btn tsp-btn-outline-primary tsp-btn-sm">
          Open the journey
        </Link>
      )}
    </div>
  );
}
